/**
 * Navigation Configuration
 * Metadata-driven menu structure organised by:
 *
 *  1. Platform Administration          (platform admin only)
 *  2. Command Center                   (universal — all authenticated users)
 *       Dashboard, Mission Control, Ask Nabhos
 *       NOTE: Satellite Monitoring is NOT here — it lives inside each vertical.
 *  3. Vertical modules                 (enabled per organisation)
 *       Each vertical owns the complete 9-step intelligence loop:
 *         [Monitoring]       → Steps 1-4  (boundaries, temporal, anomaly + verify)
 *         [Drone Operations] → Steps 5-7  (survey plan, flight analysis, verify)
 *         [Action]           → Step 8     (remediation / response / enforcement)
 *       AgroTech Estate Intelligence   (mvp1_agrotech)
 *       AgroTech Precision Planning    (mvp2_agrotech_precision)
 *       Oil & Gas Intelligence         (mvp3_oil_gas)
 *       Infrastructure Intelligence    (mvp4_infrastructure)
 *       Environmental Geotech          (mvp5_environmental)
 *  4. Organisation Settings            (org admin only — cross-vertical)
 *
 * Design principle
 * ─────────────────
 * Command Center = only items with value regardless of which vertical is active.
 * Vertical = owns the full satellite → anomaly → drone → remediation loop with
 *            its own labels, metadata fields, index set, and action vocabulary.
 *
 * Confirmed architecture decisions (Jul 2026):
 *   - Satellite monitoring moves out of Command Center into each vertical
 *   - Each vertical has its own Anomaly Alert queue (no unified cross-vertical queue)
 *   - Ask Nabhos stays universal in Command Center (full workflow context)
 *   - Drone Technician upload access is via Mission Control (no separate Telemetry Hub)
 *   - Shared React components parameterised by vertical context (one codebase, five nav contexts)
 *
 * Module keys here must mirror organizations.enabled_modules[] in the database.
 */

import { NavigationConfig } from '../types/navigation.types';

export const NAVIGATION_CONFIG: NavigationConfig[] = [

  // ===========================================================================
  // 1. COMMAND CENTER  (Universal — all authenticated users)
  //
  //  Contains ONLY items with value regardless of which vertical is active.
  //  Satellite Monitoring has been moved into each vertical group (Jul 2026).
  //  See §9 of REQUIREMENTS-Nabhos-SatelliteToDrone-V0.3.md for rationale.
  // ===========================================================================
  {
    id: 'command-center',
    label: 'Command Center',
    icon: 'LayoutDashboard',
    category: 'Command Center',
    requiredPermission: ['*.*', 'analytics:view', 'missions:view'],
    requiredModule: 'mvp0_core',
    ariaLabel: 'Operational command center — universal across all verticals',
    mobileVisibility: 'show',
    searchKeywords: ['dashboard', 'command', 'control', 'operations'],
    searchPriority: 100,
    telemetryId: 'nav_command_center',
    children: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: 'Home',
        path: '/dashboard',
        category: 'Command Center',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Main operational dashboard — cross-vertical KPI summary',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'home', 'overview', 'stats'],
        searchPriority: 99,
        telemetryId: 'nav_dashboard',
      },
      {
        // Cross-vertical flat list of all missions (survey + remediation).
        // Drone Technicians use this as their dispatch view.
        id: 'missions',
        label: 'Mission Control',
        icon: 'MapPin',
        path: '/missions',
        category: 'Command Center',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Cross-vertical mission list — all survey and remediation missions',
        mobileVisibility: 'show',
        searchKeywords: ['missions', 'flights', 'drone', 'telemetry', 'survey', 'dispatch'],
        searchPriority: 95,
        telemetryId: 'nav_missions',
      },
      {
        // AI Analysis — data intelligence workspace: mission history, NLQ, source citations,
        // mission briefings. Step 9 of the intelligence loop.
        id: 'ask-nabhos',
        label: 'AI Analysis',
        icon: 'MessageSquare',
        path: '/analysis',
        category: 'Command Center',
        requiredPermission: ['*.*', 'analytics:view', 'ai:query'],
        requiredModule: 'mvp0_core',
        ariaLabel: 'AI Analysis — data intelligence workspace across all verticals',
        mobileVisibility: 'show',
        searchKeywords: ['ai', 'ask', 'nlq', 'chat', 'insights', 'analysis', 'satellite', 'drone'],
        searchPriority: 82,
        telemetryId: 'nav_ai_analysis',
      },
    ],
  },

  // ===========================================================================
  // 3a. AGROTECH — ESTATE INTELLIGENCE  (mvp1_agrotech)
  //
  //  Owns the complete 9-step intelligence loop for agricultural operations:
  //
  //  Field Monitoring  (Steps 1–4)
  //    Field Boundaries    → Step 1: draw field boundary + crop/irrigation metadata
  //    Temporal Analysis   → Step 2: NDVI/NDRE/NDWI/LST satellite timeline
  //    Anomaly Alerts      → Step 3: AI probable-cause + Step 4: human verification
  //
  //  Drone Operations  (Steps 5–7)
  //    Survey Mission Planner  → Step 5: payload rec (multispectral/thermal) + flight path
  //    Flight Data & Analysis  → Step 6: upload + drone AI diagnosis
  //    Analysis Review         → Step 7: human verification gate 2 + agronomist override
  //
  //  Remediation Planning  (Step 8)
  //    Remediation Missions    → Step 8: spray/fertiliser/marker/ground-team plans
  //    Treatment Efficacy      → Step 8.6: verification re-survey results
  // ===========================================================================
  {
    id: 'agrotech-estate',
    label: 'Estate Intelligence',
    icon: 'Leaf',
    category: 'AgroTech',
    requiredModule: 'mvp1_agrotech',
    requiredPermission: ['*.*', 'analytics:view', 'missions:view'],
    ariaLabel: 'AgroTech estate intelligence — full satellite-to-remediation loop',
    mobileVisibility: 'show',
    searchKeywords: ['agrotech', 'agriculture', 'crop', 'estate', 'farm', 'field', 'health'],
    searchPriority: 85,
    telemetryId: 'nav_agrotech_estate',
    children: [

      // ── Operations Dashboard ──────────────────────────────────────────────
      {
        id: 'agro-dashboard',
        label: 'Operations Dashboard',
        icon: 'LayoutDashboard',
        path: '/agrotech/dashboard',
        category: 'AgroTech',
        requiredModule: 'mvp1_agrotech',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'AgroTech estate operations overview — cross-step KPI summary',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'kpi', 'overview', 'operations', 'estate', 'summary'],
        searchPriority: 86,
        telemetryId: 'nav_agro_dashboard',
      },

      // ── Field Monitoring (Steps 1–4) ──────────────────────────────────────
      {
        id: 'agro-field-monitoring',
        label: 'Field Monitoring',
        icon: 'Satellite',
        category: 'AgroTech',
        requiredModule: 'mvp1_agrotech',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Field satellite monitoring — boundaries, temporal analysis, anomaly alerts',
        mobileVisibility: 'show',
        searchKeywords: ['satellite', 'ndvi', 'ndre', 'ndwi', 'field', 'monitoring', 'boundary', 'temporal'],
        searchPriority: 84,
        telemetryId: 'nav_agro_field_monitoring',
        children: [
          {
            // Step 1: draw field polygon + enter crop type, nickname, planting date, irrigation
            id: 'agro-field-boundaries',
            label: 'Fields',
            icon: 'PenTool',
            path: '/agrotech/fields',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Draw and manage field boundaries — crop type, nickname, irrigation metadata',
            mobileVisibility: 'show',
            searchKeywords: ['field', 'boundary', 'polygon', 'draw', 'crop type', 'geojson', 'kml'],
            searchPriority: 83,
            telemetryId: 'nav_agro_field_boundaries',
          },
          {
            // Step 2: satellite imagery timeline — NDVI/NDRE/NDWI/LST layer toggles
            id: 'agro-temporal-analysis',
            label: 'Temporal Analysis',
            icon: 'Clock',
            path: '/agrotech/temporal',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Satellite index timeline — NDVI, NDRE, NDWI, LST with change slider',
            mobileVisibility: 'show',
            searchKeywords: ['temporal', 'ndvi', 'ndre', 'ndwi', 'timeline', 'satellite', 'imagery', 'change'],
            searchPriority: 82,
            telemetryId: 'nav_agro_temporal',
          },
          {
            // Step 3 (AI anomaly narrative) + Step 4 (human verification gate 1)
            id: 'agro-anomaly-alerts',
            label: 'Anomaly Alerts',
            icon: 'AlertTriangle',
            path: '/agrotech/anomalies',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'AI-flagged crop anomalies — probable cause analysis and human verification',
            mobileVisibility: 'show',
            searchKeywords: ['anomaly', 'alert', 'ndvi drop', 'waterlogging', 'stress', 'verify', 'confirm'],
            searchPriority: 81,
            telemetryId: 'nav_agro_anomalies',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/anomalies?vertical=agrotech&status=pending',
              color: 'bg-amber-500',
            },
          },
        ],
      },

      // ── Drone Operations (Steps 5–7) ──────────────────────────────────────
      {
        id: 'agro-drone-operations',
        label: 'Drone Operations',
        icon: 'Navigation',
        category: 'AgroTech',
        requiredModule: 'mvp1_agrotech',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Drone survey planning, flight data analysis, and human review',
        mobileVisibility: 'show',
        searchKeywords: ['drone', 'survey', 'mission', 'flight', 'payload', 'multispectral', 'thermal', 'lidar'],
        searchPriority: 80,
        telemetryId: 'nav_agro_drone_ops',
        children: [
          {
            // Step 5: AI pre-populates payload (multispectral/thermal/LiDAR), altitude, waypoints
            id: 'agro-survey-planner',
            label: 'Survey Mission Planner',
            icon: 'Route',
            path: '/agrotech/survey-planner',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan drone survey — AI payload recommendation, flight path, weather window',
            mobileVisibility: 'show',
            searchKeywords: ['survey', 'planner', 'payload', 'multispectral', 'flight path', 'altitude', 'gsd'],
            searchPriority: 79,
            telemetryId: 'nav_agro_survey_planner',
          },
          {
            // Step 6: Drone Technician uploads data; AI runs diagnosis; satellite-vs-drone correlation
            id: 'agro-flight-analysis',
            label: 'Flight Data & Analysis',
            icon: 'Microscope',
            path: '/agrotech/flight-analysis',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'telemetry:upload', 'missions:view'],
            ariaLabel: 'Upload drone imagery and review AI diagnosis — fungal, waterlogging, pest, nutrient',
            mobileVisibility: 'show',
            searchKeywords: ['upload', 'drone', 'analysis', 'diagnosis', 'fungal', 'pest', 'waterlogging', 'ai'],
            searchPriority: 78,
            telemetryId: 'nav_agro_flight_analysis',
          },
          {
            // Step 7: Operations Manager reviews AI drone diagnosis; confirm/override/request re-survey
            id: 'agro-analysis-review',
            label: 'Analysis Review',
            icon: 'ClipboardCheck',
            path: '/agrotech/analysis-review',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Human verification of drone AI findings — confirm, override, or escalate',
            mobileVisibility: 'show',
            searchKeywords: ['review', 'verify', 'confirm', 'override', 'diagnosis', 'human', 'hitl'],
            searchPriority: 77,
            telemetryId: 'nav_agro_analysis_review',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/drone-analyses?vertical=agrotech&status=pending_review',
              color: 'bg-blue-500',
            },
          },
        ],
      },

      // ── Remediation Planning (Step 8) ─────────────────────────────────────
      {
        id: 'agro-remediation',
        label: 'Remediation Planning',
        icon: 'Syringe',
        category: 'AgroTech',
        requiredModule: 'mvp1_agrotech',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Plan and track remediation missions — spray, fertiliser, ground team',
        mobileVisibility: 'show',
        searchKeywords: ['remediation', 'spray', 'fertiliser', 'pesticide', 'fungicide', 'treatment', 'chemical'],
        searchPriority: 76,
        telemetryId: 'nav_agro_remediation',
        children: [
          {
            // Step 8: AI pre-populates spray drone, chemical, rate, no-spray buffer, dosage check
            id: 'agro-remediation-missions',
            label: 'Remediation Missions',
            icon: 'Droplets',
            path: '/agrotech/remediation',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan precision spray, fertiliser, or ground-team remediation missions',
            mobileVisibility: 'show',
            searchKeywords: ['remediation', 'spray', 'drone', 'prescription', 'chemical', 'dosage', 'plan'],
            searchPriority: 75,
            telemetryId: 'nav_agro_remediation_missions',
          },
          {
            // Step 8.6: 14-day re-survey results; NDVI recovery score; treatment efficacy
            id: 'agro-treatment-efficacy',
            label: 'Treatment Efficacy',
            icon: 'TrendingUp',
            path: '/agrotech/efficacy',
            category: 'AgroTech',
            requiredModule: 'mvp1_agrotech',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Verification re-survey results — NDVI recovery and treatment efficacy scores',
            mobileVisibility: 'show',
            searchKeywords: ['efficacy', 'recovery', 'ndvi', 'treatment', 'result', 'verification'],
            searchPriority: 74,
            telemetryId: 'nav_agro_efficacy',
          },
        ],
      },

      // ── Reports ───────────────────────────────────────────────────────────
      {
        id: 'agro-reports',
        label: 'Crop Health Reports',
        icon: 'FileText',
        path: '/agrotech/reports',
        category: 'AgroTech',
        requiredModule: 'mvp1_agrotech',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Certified crop health and mission reports — PDF export',
        mobileVisibility: 'show',
        searchKeywords: ['report', 'crop', 'health', 'certified', 'pdf', 'export', 'audit'],
        searchPriority: 73,
        telemetryId: 'nav_agro_reports',
      },
    ],
  },

  // ===========================================================================
  // 3b. AGROTECH — PRECISION PLANNING  (mvp2_agrotech_precision)
  // ===========================================================================
  {
    id: 'agrotech-precision',
    label: 'Precision Planning',
    icon: 'Target',
    category: 'AgroTech Precision',
    requiredModule: 'mvp2_agrotech_precision',
    requiredPermission: ['*.*', 'precision:execute'],
    ariaLabel: 'AgroTech precision planning — VRA, yield forecasting, crop suitability',
    mobileVisibility: 'collapse',
    searchKeywords: ['precision', 'agriculture', 'variable-rate', 'yield', 'prescription', 'isoxml'],
    searchPriority: 72,
    telemetryId: 'nav_agrotech_precision',
    children: [
      {
        id: 'variable-rate',
        label: 'Variable-Rate Scripting',
        icon: 'Layers',
        path: '/precision/variable-rate',
        category: 'AgroTech Precision',
        requiredModule: 'mvp2_agrotech_precision',
        requiredPermission: ['*.*', 'precision:execute'],
        ariaLabel: 'Variable-rate application prescription maps',
        mobileVisibility: 'show',
        searchKeywords: ['variable-rate', 'vra', 'prescription', 'application', 'isoxml'],
        searchPriority: 71,
        telemetryId: 'nav_variable_rate',
      },
      {
        id: 'yield-forecasting',
        label: 'Yield Forecasting',
        icon: 'TrendingUp',
        path: '/precision/yield',
        category: 'AgroTech Precision',
        requiredModule: 'mvp2_agrotech_precision',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'AI-powered yield predictions and performance benchmarking',
        mobileVisibility: 'show',
        searchKeywords: ['yield', 'forecast', 'prediction', 'harvest', 'benchmark'],
        searchPriority: 70,
        telemetryId: 'nav_yield_forecasting',
      },
      {
        id: 'crop-suitability',
        label: 'Crop Suitability Index',
        icon: 'BarChart2',
        path: '/precision/crop-suitability',
        category: 'AgroTech Precision',
        requiredModule: 'mvp2_agrotech_precision',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'High-value crop suitability scoring (Kiwi, Avocado, etc.)',
        mobileVisibility: 'show',
        searchKeywords: ['suitability', 'kiwi', 'avocado', 'crop transition', 'soil', 'index'],
        searchPriority: 69,
        telemetryId: 'nav_crop_suitability',
      },
      {
        id: 'land-expansion',
        label: 'Land Expansion Modelling',
        icon: 'Expand',
        path: '/precision/land-expansion',
        category: 'AgroTech Precision',
        requiredModule: 'mvp2_agrotech_precision',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Site suitability analysis for new land acquisitions',
        mobileVisibility: 'show',
        searchKeywords: ['land', 'expansion', 'acquisition', 'suitability', 'roi', 'npv'],
        searchPriority: 67,
        telemetryId: 'nav_land_expansion',
      },
      {
        id: 'chemical-compliance',
        label: 'Chemical Compliance',
        icon: 'ShieldAlert',
        path: '/precision/compliance',
        category: 'AgroTech Precision',
        requiredModule: 'mvp2_agrotech_precision',
        requiredPermission: ['*.*', 'precision:execute'],
        ariaLabel: 'Chemical dosage compliance and regulatory safety guardrails',
        mobileVisibility: 'show',
        searchKeywords: ['compliance', 'chemical', 'safety', 'dosage', 'regulatory', 'pesticide'],
        searchPriority: 65,
        telemetryId: 'nav_chemical_compliance',
      },
    ],
  },

  // ===========================================================================
  // 3c. OIL & GAS INTELLIGENCE  (mvp3_oil_gas)
  //
  //  Follows the same 3-subgroup pattern as AgroTech:
  //    Corridor Monitoring  → Steps 1–4 (pipeline boundaries, temporal, anomalies)
  //    Drone Operations     → Steps 5–7 (OGI/thermal survey, analysis, review)
  //    Incident Response    → Step 8    (ground team / pressure test / shut-in)
  // ===========================================================================
  {
    id: 'oil-gas',
    label: 'Oil & Gas Intelligence',
    icon: 'Flame',
    category: 'Oil & Gas',
    requiredModule: 'mvp3_oil_gas',
    requiredPermission: ['*.*', 'analytics:view'],
    ariaLabel: 'Oil & Gas — pipeline right-of-way monitoring and methane detection',
    mobileVisibility: 'collapse',
    searchKeywords: ['oil', 'gas', 'pipeline', 'methane', 'thermal', 'encroachment', 'ogi'],
    searchPriority: 70,
    telemetryId: 'nav_oil_gas',
    children: [

      // ── O&G Operations Dashboard ───────────────────────────────────────────
      {
        id: 'og-dashboard',
        label: 'Operations Dashboard',
        icon: 'LayoutDashboard',
        path: '/oil-gas/dashboard',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Oil & Gas operations overview — cross-step KPI summary',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'kpi', 'overview', 'operations', 'pipeline', 'summary'],
        searchPriority: 70,
        telemetryId: 'nav_og_dashboard',
      },

      // ── Corridor Monitoring (Steps 1–4) ───────────────────────────────────
      {
        id: 'og-corridor-monitoring',
        label: 'Corridor Monitoring',
        icon: 'Satellite',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Pipeline corridor satellite monitoring — boundaries, temporal, anomaly alerts',
        mobileVisibility: 'show',
        searchKeywords: ['pipeline', 'corridor', 'satellite', 'monitoring', 'thermal', 'ndvi', 'leak'],
        searchPriority: 69,
        telemetryId: 'nav_og_corridor_monitoring',
        children: [
          {
            id: 'og-corridor-boundaries',
            label: 'Corridor Boundaries',
            icon: 'PenTool',
            path: '/oil-gas/boundaries',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Define pipeline corridor boundaries — pipeline ID, buffer width, product type',
            mobileVisibility: 'show',
            searchKeywords: ['corridor', 'boundary', 'pipeline', 'right-of-way', 'buffer'],
            searchPriority: 68,
            telemetryId: 'nav_og_boundaries',
          },
          {
            id: 'og-temporal-analysis',
            label: 'Temporal Analysis',
            icon: 'Clock',
            path: '/oil-gas/temporal',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Corridor satellite timeline — LST, NDVI die-off, thermal anomaly layers',
            mobileVisibility: 'show',
            searchKeywords: ['temporal', 'lst', 'thermal', 'ndvi', 'die-off', 'timeline', 'satellite'],
            searchPriority: 67,
            telemetryId: 'nav_og_temporal',
          },
          {
            id: 'og-anomaly-alerts',
            label: 'Anomaly Alerts',
            icon: 'AlertTriangle',
            path: '/oil-gas/anomalies',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Pipeline anomaly alerts — leak indicators, thermal spikes, encroachment',
            mobileVisibility: 'show',
            searchKeywords: ['anomaly', 'leak', 'thermal', 'vegetation die-off', 'encroachment', 'critical'],
            searchPriority: 66,
            telemetryId: 'nav_og_anomalies',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/anomalies?vertical=oil_gas&status=pending',
              color: 'bg-red-500',
            },
          },
        ],
      },

      // ── Drone Operations (Steps 5–7) ──────────────────────────────────────
      {
        id: 'og-drone-operations',
        label: 'Drone Operations',
        icon: 'Navigation',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'OGI and thermal drone survey planning and analysis',
        mobileVisibility: 'show',
        searchKeywords: ['drone', 'ogi', 'thermal', 'survey', 'methane', 'payload', 'mission'],
        searchPriority: 65,
        telemetryId: 'nav_og_drone_ops',
        children: [
          {
            id: 'og-survey-planner',
            label: 'Survey Mission Planner',
            icon: 'Route',
            path: '/oil-gas/survey-planner',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan OGI/thermal drone survey — wind window, low-altitude corridor flight',
            mobileVisibility: 'show',
            searchKeywords: ['ogi', 'thermal', 'survey', 'planner', 'wind', 'corridor', 'methane'],
            searchPriority: 64,
            telemetryId: 'nav_og_survey_planner',
          },
          {
            id: 'og-flight-analysis',
            label: 'Flight Data & Analysis',
            icon: 'Microscope',
            path: '/oil-gas/flight-analysis',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'telemetry:upload', 'missions:view'],
            ariaLabel: 'Upload OGI/thermal imagery and review AI leak / hotspot diagnosis',
            mobileVisibility: 'show',
            searchKeywords: ['ogi', 'thermal', 'upload', 'analysis', 'methane', 'leak', 'hotspot'],
            searchPriority: 63,
            telemetryId: 'nav_og_flight_analysis',
          },
          {
            id: 'og-analysis-review',
            label: 'Analysis Review',
            icon: 'ClipboardCheck',
            path: '/oil-gas/analysis-review',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Human verification of pipeline drone findings — confirm or escalate to safety team',
            mobileVisibility: 'show',
            searchKeywords: ['review', 'verify', 'leak', 'safety', 'escalate', 'confirm'],
            searchPriority: 62,
            telemetryId: 'nav_og_analysis_review',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/drone-analyses?vertical=oil_gas&status=pending_review',
              color: 'bg-blue-500',
            },
          },
        ],
      },

      // ── Incident Response (Step 8) ─────────────────────────────────────────
      {
        id: 'og-incident-response',
        label: 'Incident Response',
        icon: 'AlertOctagon',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Pipeline incident response — ground team dispatch, pressure test, shut-in',
        mobileVisibility: 'show',
        searchKeywords: ['incident', 'response', 'pressure', 'test', 'shut-in', 'ground team', 'emergency'],
        searchPriority: 61,
        telemetryId: 'nav_og_incident_response',
        children: [
          {
            id: 'og-response-planning',
            label: 'Response Planning',
            icon: 'ClipboardList',
            path: '/oil-gas/response',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan ground team response, pressure testing, or shut-in procedure',
            mobileVisibility: 'show',
            searchKeywords: ['response', 'plan', 'pressure', 'shut-in', 'ground team', 'procedure'],
            searchPriority: 60,
            telemetryId: 'nav_og_response_planning',
          },
          {
            id: 'og-incident-closure',
            label: 'Incident Closure',
            icon: 'CheckCircle2',
            path: '/oil-gas/closure',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Close resolved incidents — verification evidence and audit linkage',
            mobileVisibility: 'show',
            searchKeywords: ['closure', 'resolved', 'incident', 'verification', 'audit'],
            searchPriority: 59,
            telemetryId: 'nav_og_closure',
          },
        ],
      },

      // ── Upstream Exploration ───────────────────────────────────────────────
      {
        id: 'upstream-exploration',
        label: 'Upstream Exploration',
        icon: 'Search',
        path: '/oil-gas/exploration',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Magnetometry and seismic exploration analysis',
        mobileVisibility: 'show',
        searchKeywords: ['upstream', 'exploration', 'magnetometry', 'seismic', 'geology'],
        searchPriority: 57,
        telemetryId: 'nav_upstream_exploration',
      },

      // ── Deep Analysis subgroup ─────────────────────────────────────────────
      {
        id: 'og-deep-analysis',
        label: 'Deep Analysis',
        icon: 'FlaskConical',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'OGI plume telemetry and facility thermal monitoring',
        mobileVisibility: 'show',
        searchKeywords: ['plume', 'ch4', 'methane', 'facility', 'flaring', 'thermal', 'analysis'],
        searchPriority: 56,
        telemetryId: 'nav_og_deep_analysis',
        children: [
          {
            id: 'og-plume-analyzer',
            label: 'Plume & Leak Analyzer',
            icon: 'Wind',
            path: '/oil-gas/plume-analyzer',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'OGI telemetry plume bounding and CH₄ flow-rate quantification',
            mobileVisibility: 'show',
            searchKeywords: ['plume', 'leak', 'ogi', 'methane', 'ch4', 'flow rate', 'bounding'],
            searchPriority: 55,
            telemetryId: 'nav_og_plume_analyzer',
          },
          {
            id: 'og-facility-safety',
            label: 'Facility Safety & Flaring',
            icon: 'Flame',
            path: '/oil-gas/facility-safety',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Thermal baseline tracking and flare intensity monitoring',
            mobileVisibility: 'show',
            searchKeywords: ['facility', 'flaring', 'thermal', 'baseline', 'flame', 'offshore', 'safety'],
            searchPriority: 54,
            telemetryId: 'nav_og_facility_safety',
          },
        ],
      },

      // ── Incident Management subgroup ──────────────────────────────────────
      {
        id: 'og-incident-management',
        label: 'Incident Management',
        icon: 'BarChart2',
        category: 'Oil & Gas',
        requiredModule: 'mvp3_oil_gas',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'ERP work order audit and ROI value realization reporting',
        mobileVisibility: 'show',
        searchKeywords: ['erp', 'sap', 'oracle', 'roi', 'value', 'savings', 'regulatory', 'reporting'],
        searchPriority: 53,
        telemetryId: 'nav_og_incident_mgmt',
        children: [
          {
            id: 'og-erp-integration',
            label: 'ERP Integration Hub',
            icon: 'Plug',
            path: '/oil-gas/erp-integration',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'SAP / Oracle work order dispatch audit trail',
            mobileVisibility: 'show',
            searchKeywords: ['erp', 'sap', 'oracle', 'work order', 'integration', 'dlq', 'connector'],
            searchPriority: 52,
            telemetryId: 'nav_og_erp_integration',
          },
          {
            id: 'og-value-realization',
            label: 'Value Realization & ROI',
            icon: 'TrendingUp',
            path: '/oil-gas/value-realization',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Product loss quantification and fine avoidance ROI analytics',
            mobileVisibility: 'show',
            searchKeywords: ['roi', 'value', 'loss', 'fine', 'savings', 'emissions', 'carbon', 'analytics'],
            searchPriority: 51,
            telemetryId: 'nav_og_value_realization',
          },
          {
            id: 'og-regulatory',
            label: 'Regulatory Reporting',
            icon: 'FileText',
            path: '/oil-gas/regulatory',
            category: 'Oil & Gas',
            requiredModule: 'mvp3_oil_gas',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Compile SHA-256 sealed closure records into regulatory evidence packs',
            mobileVisibility: 'show',
            searchKeywords: ['regulatory', 'reporting', 'dpr', 'nuprc', 'epa', 'compliance', 'evidence', 'audit', 'submission'],
            searchPriority: 50,
            telemetryId: 'nav_og_regulatory',
          },
        ],
      },
    ],
  },

  // ===========================================================================
  // 3d. INFRASTRUCTURE INTELLIGENCE  (mvp4_infrastructure)
  //
  //  Asset Monitoring     → Steps 1–4 (corridor/site boundaries, temporal, anomalies)
  //  Drone Operations     → Steps 5–7 (RGB+LiDAR survey, volume analysis, review)
  //  Compliance & Enforcement → Step 8 (stop-work / compliance verification)
  // ===========================================================================
  {
    id: 'infrastructure',
    label: 'Infrastructure Monitor',
    icon: 'Building',
    category: 'Infrastructure',
    requiredModule: 'mvp4_infrastructure',
    requiredPermission: ['*.*', 'analytics:view'],
    ariaLabel: 'Infrastructure — roads, power lines, construction progress monitoring',
    mobileVisibility: 'collapse',
    searchKeywords: ['infrastructure', 'road', 'bridge', 'power line', 'construction', 'progress', 'earthwork'],
    searchPriority: 65,
    telemetryId: 'nav_infrastructure',
    children: [

      // ── Operations Dashboard ──────────────────────────────────────────────
      {
        id: 'infra-dashboard',
        label: 'Operations Dashboard',
        icon: 'LayoutDashboard',
        path: '/infrastructure/dashboard',
        category: 'Infrastructure',
        requiredModule: 'mvp4_infrastructure',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Infrastructure operations overview — cross-step KPI summary',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'kpi', 'overview', 'operations', 'infrastructure', 'summary'],
        searchPriority: 65,
        telemetryId: 'nav_infra_dashboard',
      },

      // ── Asset Monitoring (Steps 1–4) ──────────────────────────────────────
      {
        id: 'infra-asset-monitoring',
        label: 'Asset Monitoring',
        icon: 'Satellite',
        category: 'Infrastructure',
        requiredModule: 'mvp4_infrastructure',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Asset corridor satellite monitoring — boundaries, temporal, anomaly alerts',
        mobileVisibility: 'show',
        searchKeywords: ['asset', 'corridor', 'satellite', 'monitoring', 'ndbi', 'earthwork'],
        searchPriority: 64,
        telemetryId: 'nav_infra_monitoring',
        children: [
          {
            id: 'infra-asset-boundaries',
            label: 'Asset Boundaries',
            icon: 'PenTool',
            path: '/infrastructure/boundaries',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Define infrastructure asset boundaries — corridor, construction site, slope zone',
            mobileVisibility: 'show',
            searchKeywords: ['boundary', 'corridor', 'asset', 'construction', 'site', 'road', 'railway'],
            searchPriority: 63,
            telemetryId: 'nav_infra_boundaries',
          },
          {
            id: 'infra-temporal-analysis',
            label: 'Temporal Analysis',
            icon: 'Clock',
            path: '/infrastructure/temporal',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Asset corridor satellite timeline — NDBI, earthwork, slope instability indices',
            mobileVisibility: 'show',
            searchKeywords: ['temporal', 'ndbi', 'earthwork', 'slope', 'construction', 'timeline'],
            searchPriority: 62,
            telemetryId: 'nav_infra_temporal',
          },
          {
            id: 'infra-anomaly-alerts',
            label: 'Anomaly Alerts',
            icon: 'AlertTriangle',
            path: '/infrastructure/anomalies',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Infrastructure anomaly alerts — unauthorized construction, earthwork, slope instability',
            mobileVisibility: 'show',
            searchKeywords: ['anomaly', 'unauthorized', 'construction', 'earthwork', 'slope', 'encroachment'],
            searchPriority: 61,
            telemetryId: 'nav_infra_anomalies',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/anomalies?vertical=infrastructure&status=pending',
              color: 'bg-amber-500',
            },
          },
        ],
      },

      // ── Drone Operations (Steps 5–7) ──────────────────────────────────────
      {
        id: 'infra-drone-operations',
        label: 'Drone Operations',
        icon: 'Navigation',
        category: 'Infrastructure',
        requiredModule: 'mvp4_infrastructure',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'RGB + LiDAR drone survey planning and volumetric analysis',
        mobileVisibility: 'show',
        searchKeywords: ['drone', 'lidar', 'rgb', 'survey', 'volumetric', 'photogrammetry', 'earthwork'],
        searchPriority: 60,
        telemetryId: 'nav_infra_drone_ops',
        children: [
          {
            id: 'infra-survey-planner',
            label: 'Survey Mission Planner',
            icon: 'Route',
            path: '/infrastructure/survey-planner',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan RGB+LiDAR drone survey — volumetric and legal-grade photogrammetry',
            mobileVisibility: 'show',
            searchKeywords: ['survey', 'planner', 'lidar', 'rgb', 'photogrammetry', 'volume', 'legal'],
            searchPriority: 59,
            telemetryId: 'nav_infra_survey_planner',
          },
          {
            id: 'infra-flight-analysis',
            label: 'Flight Data & Analysis',
            icon: 'Microscope',
            path: '/infrastructure/flight-analysis',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'telemetry:upload', 'missions:view'],
            ariaLabel: 'Upload LiDAR/RGB and review AI earthwork volume and structural analysis',
            mobileVisibility: 'show',
            searchKeywords: ['upload', 'lidar', 'analysis', 'volume', 'earthwork', 'structural'],
            searchPriority: 58,
            telemetryId: 'nav_infra_flight_analysis',
          },
          {
            id: 'infra-analysis-review',
            label: 'Analysis Review',
            icon: 'ClipboardCheck',
            path: '/infrastructure/analysis-review',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Human verification of infrastructure drone findings — confirm or issue stop-work',
            mobileVisibility: 'show',
            searchKeywords: ['review', 'verify', 'stop-work', 'compliance', 'earthwork', 'confirm'],
            searchPriority: 57,
            telemetryId: 'nav_infra_analysis_review',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/drone-analyses?vertical=infrastructure&status=pending_review',
              color: 'bg-blue-500',
            },
          },
        ],
      },

      // ── Compliance & Enforcement (Step 8) ─────────────────────────────────
      {
        id: 'infra-compliance',
        label: 'Compliance & Enforcement',
        icon: 'Scale',
        category: 'Infrastructure',
        requiredModule: 'mvp4_infrastructure',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Infrastructure compliance — stop-work orders, construction progress, encroachment',
        mobileVisibility: 'show',
        searchKeywords: ['compliance', 'stop-work', 'enforcement', 'progress', 'encroachment', 'legal'],
        searchPriority: 56,
        telemetryId: 'nav_infra_compliance',
        children: [
          {
            id: 'infra-response-planning',
            label: 'Response Planning',
            icon: 'ClipboardList',
            path: '/infrastructure/response',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan compliance response — stop-work notices, inspection orders',
            mobileVisibility: 'show',
            searchKeywords: ['response', 'stop-work', 'compliance', 'inspection', 'order'],
            searchPriority: 55,
            telemetryId: 'nav_infra_response',
          },
          {
            id: 'construction-progress',
            label: 'Construction Progress',
            icon: 'HardHat',
            path: '/infrastructure/construction',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Construction progress tracking and earthwork volumetrics',
            mobileVisibility: 'show',
            searchKeywords: ['construction', 'progress', 'earthwork', 'volumetric', 'cut-fill'],
            searchPriority: 54,
            telemetryId: 'nav_construction_progress',
          },
          {
            id: 'unauthorized-encroachment',
            label: 'Encroachment Detection',
            icon: 'AlertOctagon',
            path: '/infrastructure/encroachment',
            category: 'Infrastructure',
            requiredModule: 'mvp4_infrastructure',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Detect unauthorized construction and corridor encroachment',
            mobileVisibility: 'show',
            searchKeywords: ['encroachment', 'unauthorized', 'construction', 'compliance', 'NDBI'],
            searchPriority: 52,
            telemetryId: 'nav_unauthorized_encroachment',
          },
        ],
      },
    ],
  },

  // ===========================================================================
  // 3e. ENVIRONMENTAL GEOTECH  (mvp5_environmental)
  //
  //  Zone Monitoring       → Steps 1–4 (protected zone boundaries, temporal, anomalies)
  //  Drone Operations      → Steps 5–7 (fixed-wing RGB+multispectral, analysis, review)
  //  Enforcement & Reporting → Step 8 (authority notification, carbon verification)
  // ===========================================================================
  {
    id: 'environmental',
    label: 'Environmental Monitor',
    icon: 'TreePine',
    category: 'Environmental',
    requiredModule: 'mvp5_environmental',
    requiredPermission: ['*.*', 'analytics:view'],
    ariaLabel: 'Environmental geotech — deforestation, wetlands, geohazard, carbon',
    mobileVisibility: 'collapse',
    searchKeywords: ['environmental', 'deforestation', 'wetland', 'landslide', 'carbon', 'geotech', 'forest'],
    searchPriority: 60,
    telemetryId: 'nav_environmental',
    children: [

      // ── Operations Dashboard ──────────────────────────────────────────────
      {
        id: 'env-dashboard',
        label: 'Operations Dashboard',
        icon: 'LayoutDashboard',
        path: '/environmental/dashboard',
        category: 'Environmental',
        requiredModule: 'mvp5_environmental',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Environmental operations overview — cross-step KPI summary',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'kpi', 'overview', 'operations', 'environmental', 'summary'],
        searchPriority: 60,
        telemetryId: 'nav_env_dashboard',
      },

      // ── Zone Monitoring (Steps 1–4) ───────────────────────────────────────
      {
        id: 'env-zone-monitoring',
        label: 'Zone Monitoring',
        icon: 'Satellite',
        category: 'Environmental',
        requiredModule: 'mvp5_environmental',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Protected zone satellite monitoring — boundaries, temporal, anomaly alerts',
        mobileVisibility: 'show',
        searchKeywords: ['zone', 'satellite', 'monitoring', 'deforestation', 'ndwi', 'canopy', 'wetland'],
        searchPriority: 59,
        telemetryId: 'nav_env_zone_monitoring',
        children: [
          {
            id: 'env-zone-boundaries',
            label: 'Zone Boundaries',
            icon: 'PenTool',
            path: '/environmental/boundaries',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Define protected zone boundaries — Ramsar, national park, carbon credit parcel',
            mobileVisibility: 'show',
            searchKeywords: ['zone', 'boundary', 'ramsar', 'national park', 'carbon', 'protected', 'forest'],
            searchPriority: 58,
            telemetryId: 'nav_env_boundaries',
          },
          {
            id: 'env-temporal-analysis',
            label: 'Temporal Analysis',
            icon: 'Clock',
            path: '/environmental/temporal',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Zone satellite timeline — canopy cover, NDWI wetland, burn indices',
            mobileVisibility: 'show',
            searchKeywords: ['temporal', 'canopy', 'ndwi', 'nbr', 'deforestation', 'wetland', 'timeline'],
            searchPriority: 57,
            telemetryId: 'nav_env_temporal',
          },
          {
            id: 'env-anomaly-alerts',
            label: 'Anomaly Alerts',
            icon: 'AlertTriangle',
            path: '/environmental/anomalies',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Environmental anomaly alerts — deforestation, wetland encroachment, illegal mining',
            mobileVisibility: 'show',
            searchKeywords: ['anomaly', 'deforestation', 'clearing', 'wetland', 'illegal', 'mining', 'enforcement'],
            searchPriority: 56,
            telemetryId: 'nav_env_anomalies',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/anomalies?vertical=environmental&status=pending',
              color: 'bg-amber-500',
            },
          },
        ],
      },

      // ── Drone Operations (Steps 5–7) ──────────────────────────────────────
      {
        id: 'env-drone-operations',
        label: 'Drone Operations',
        icon: 'Navigation',
        category: 'Environmental',
        requiredModule: 'mvp5_environmental',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Fixed-wing RGB + multispectral survey planning and canopy analysis',
        mobileVisibility: 'show',
        searchKeywords: ['drone', 'fixed-wing', 'rgb', 'multispectral', 'canopy', 'survey', 'deforestation'],
        searchPriority: 55,
        telemetryId: 'nav_env_drone_ops',
        children: [
          {
            id: 'env-survey-planner',
            label: 'Survey Mission Planner',
            icon: 'Route',
            path: '/environmental/survey-planner',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Plan fixed-wing canopy survey — RGB + multispectral, wide-area coverage',
            mobileVisibility: 'show',
            searchKeywords: ['survey', 'planner', 'fixed-wing', 'canopy', 'multispectral', 'rgb'],
            searchPriority: 54,
            telemetryId: 'nav_env_survey_planner',
          },
          {
            id: 'env-flight-analysis',
            label: 'Flight Data & Analysis',
            icon: 'Microscope',
            path: '/environmental/flight-analysis',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'telemetry:upload', 'missions:view'],
            ariaLabel: 'Upload canopy imagery and review AI deforestation / encroachment diagnosis',
            mobileVisibility: 'show',
            searchKeywords: ['upload', 'canopy', 'analysis', 'deforestation', 'clearing', 'wetland'],
            searchPriority: 53,
            telemetryId: 'nav_env_flight_analysis',
          },
          {
            id: 'env-analysis-review',
            label: 'Analysis Review',
            icon: 'ClipboardCheck',
            path: '/environmental/analysis-review',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Human verification of environmental drone findings — confirm or notify enforcement',
            mobileVisibility: 'show',
            searchKeywords: ['review', 'verify', 'enforcement', 'authority', 'confirm', 'deforestation'],
            searchPriority: 52,
            telemetryId: 'nav_env_analysis_review',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/drone-analyses?vertical=environmental&status=pending_review',
              color: 'bg-blue-500',
            },
          },
        ],
      },

      // ── Enforcement & Reporting (Step 8) ──────────────────────────────────
      {
        id: 'env-enforcement',
        label: 'Enforcement & Reporting',
        icon: 'Scale',
        category: 'Environmental',
        requiredModule: 'mvp5_environmental',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Environmental enforcement — authority notification, legal documentation, carbon audit',
        mobileVisibility: 'show',
        searchKeywords: ['enforcement', 'authority', 'legal', 'document', 'carbon', 'audit', 'regulation'],
        searchPriority: 51,
        telemetryId: 'nav_env_enforcement',
        children: [
          {
            id: 'env-enforcement-actions',
            label: 'Enforcement Actions',
            icon: 'ClipboardList',
            path: '/environmental/enforcement',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'missions:view'],
            ariaLabel: 'Log and dispatch enforcement actions — notify authority, attach evidence',
            mobileVisibility: 'show',
            searchKeywords: ['enforcement', 'action', 'authority', 'notify', 'legal', 'evidence'],
            searchPriority: 50,
            telemetryId: 'nav_env_enforcement_actions',
          },
          {
            id: 'carbon-credits',
            label: 'Carbon Credit Verification',
            icon: 'Leaf',
            path: '/environmental/carbon',
            category: 'Environmental',
            requiredModule: 'mvp5_environmental',
            requiredPermission: ['*.*', 'analytics:view'],
            ariaLabel: 'Carbon credit verification and biomass monitoring',
            mobileVisibility: 'show',
            searchKeywords: ['carbon', 'credits', 'biomass', 'offset', 'verification', 'sequestration'],
            searchPriority: 49,
            telemetryId: 'nav_carbon_credits',
          },
        ],
      },

      // ── Geohazard Risk (existing; unchanged) ──────────────────────────────
      {
        id: 'geohazard-risk',
        label: 'Geohazard Risk',
        icon: 'Mountain',
        path: '/environmental/geohazard',
        category: 'Environmental',
        requiredModule: 'mvp5_environmental',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Landslide detection, flood risk, and slope stability assessment',
        mobileVisibility: 'show',
        searchKeywords: ['landslide', 'flood', 'slope', 'geohazard', 'risk', 'dem'],
        searchPriority: 47,
        telemetryId: 'nav_geohazard',
      },
    ],
  },

  // ===========================================================================
  // 3e. LINEAR ASSETS — POWER LINES & RAILWAYS (mvp6_linear_assets)
  // ===========================================================================
  {
    id: 'linear-assets-vertical',
    label: 'Linear Assets (Power & Rail)',
    icon: 'Zap',
    category: 'Linear Assets',
    requiredPermission: ['*.*', 'analytics:view', 'missions:view'],
    requiredModule: 'mvp0_core',
    ariaLabel: 'Linear assets intelligence — power line and railway corridor surveillance',
    mobileVisibility: 'show',
    searchKeywords: ['linear', 'power', 'railway', 'corridor', 'encroachment', 'obstruction'],
    searchPriority: 70,
    telemetryId: 'nav_linear_assets',
    children: [
      {
        id: 'lin-dashboard',
        label: 'Operations Dashboard',
        icon: 'LayoutDashboard',
        path: '/linear-assets/dashboard',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Linear assets operations overview',
        mobileVisibility: 'show',
        searchKeywords: ['dashboard', 'linear', 'corridor', 'overview'],
        searchPriority: 69,
        telemetryId: 'nav_lin_dashboard',
      },
      {
        id: 'lin-corridors',
        label: 'Corridor Boundaries',
        icon: 'Route',
        path: '/linear-assets/corridors',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Corridor centrelines and buffer boundaries',
        mobileVisibility: 'show',
        searchKeywords: ['corridor', 'boundary', 'pylon', 'track'],
        searchPriority: 68,
        telemetryId: 'nav_lin_corridors',
      },
      {
        id: 'lin-temporal',
        label: 'Temporal Analysis',
        icon: 'Clock',
        path: '/linear-assets/temporal',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Corridor satellite timeline',
        mobileVisibility: 'show',
        searchKeywords: ['temporal', 'timeline', 'satellite'],
        searchPriority: 67,
        telemetryId: 'nav_lin_temporal',
      },
      {
        id: 'lin-encroachment',
        label: 'Encroachment Projections',
        icon: 'TrendingUp',
        path: '/linear-assets/encroachment',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Vegetation growth velocity and contact projections',
        mobileVisibility: 'show',
        searchKeywords: ['encroachment', 'growth', 'canopy', 'days to contact'],
        searchPriority: 66,
        telemetryId: 'nav_lin_encroach',
      },
      {
        id: 'lin-obstructions',
        label: 'Track Obstruction Alerts',
        icon: 'AlertTriangle',
        path: '/linear-assets/obstructions',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Rapid optical/thermal track obstruction detection',
        mobileVisibility: 'show',
        searchKeywords: ['track', 'obstruction', 'railway', 'foreign object'],
        searchPriority: 65,
        telemetryId: 'nav_lin_obstruct',
      },
      {
        id: 'lin-survey-planner',
        label: 'Survey Planner',
        icon: 'Navigation',
        path: '/linear-assets/survey-planner',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Plan corridor strip survey flights',
        mobileVisibility: 'show',
        searchKeywords: ['survey', 'planner', 'strip flight'],
        searchPriority: 64,
        telemetryId: 'nav_lin_survey_planner',
      },
      {
        id: 'lin-flight-analysis',
        label: 'Flight Data & Diagnostics',
        icon: 'Microscope',
        path: '/linear-assets/flight-analysis',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:write', 'telemetry:upload'],
        ariaLabel: 'LiDAR and stereo RGB corridor diagnostics',
        mobileVisibility: 'show',
        searchKeywords: ['flight', 'lidar', 'point cloud', 'conductor'],
        searchPriority: 63,
        telemetryId: 'nav_lin_flight_analysis',
      },
      {
        id: 'lin-analysis-review',
        label: 'Analysis Review',
        icon: 'ClipboardCheck',
        path: '/linear-assets/analysis-review',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:write', 'hitl:certify'],
        ariaLabel: 'Engineer review and digital seal sign-off',
        mobileVisibility: 'show',
        searchKeywords: ['review', 'seal', 'sign-off'],
        searchPriority: 62,
        telemetryId: 'nav_lin_analysis_review',
      },
      {
        id: 'lin-response',
        label: 'Clearance Planning',
        icon: 'ShieldCheck',
        path: '/linear-assets/response',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:write'],
        ariaLabel: 'Vegetation trimming and crew dispatch',
        mobileVisibility: 'show',
        searchKeywords: ['response', 'clearance', 'trimming', 'dispatch'],
        searchPriority: 61,
        telemetryId: 'nav_lin_response',
      },
      {
        id: 'lin-work-orders',
        label: 'Work Orders & Audit',
        icon: 'FileText',
        path: '/linear-assets/work-orders',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'missions:view'],
        ariaLabel: 'Immutable work orders and clearance audit records',
        mobileVisibility: 'show',
        searchKeywords: ['work orders', 'audit', 'records'],
        searchPriority: 60,
        telemetryId: 'nav_lin_work_orders',
      },
      {
        id: 'lin-reports',
        label: 'Compliance Reports',
        icon: 'FileText',
        path: '/linear-assets/reports',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Certified right-of-way compliance certificates',
        mobileVisibility: 'show',
        searchKeywords: ['reports', 'compliance', 'certificate'],
        searchPriority: 59,
        telemetryId: 'nav_lin_reports',
      },
      {
        id: 'lin-efficacy',
        label: 'Clearance Efficacy',
        icon: 'Activity',
        path: '/linear-assets/efficacy',
        category: 'Linear Assets',
        requiredPermission: ['*.*', 'analytics:view'],
        ariaLabel: 'Post-clearance regrowth monitoring and verification',
        mobileVisibility: 'show',
        searchKeywords: ['efficacy', 'regrowth', 'rescan'],
        searchPriority: 58,
        telemetryId: 'nav_lin_efficacy',
      },
    ],
  },

  // ===========================================================================
  // 4. ORGANISATION SETTINGS  (Cross-vertical — org admin only)
  // ===========================================================================
  {
    id: 'organization-settings',
    label: 'Organisation Settings',
    icon: 'Settings',
    category: 'Organization Administration',
    requiredPermission: ['*.*', 'organization:admin', 'organization:*'],
    ariaLabel: 'Organisation settings and configuration — cross-vertical',
    mobileVisibility: 'collapse',
    searchKeywords: ['settings', 'organization', 'config', 'admin'],
    searchPriority: 40,
    telemetryId: 'nav_org_settings',
    dividerAfter: true,
    children: [
      {
        id: 'user-management',
        label: 'User Access Control',
        icon: 'Users',
        path: '/org-admin/users',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'users:write'],
        ariaLabel: 'Manage team members, roles, and access control',
        mobileVisibility: 'show',
        searchKeywords: ['users', 'team', 'members', 'access', 'roster', 'roles'],
        searchPriority: 39,
        telemetryId: 'nav_user_management',
      },
      {
        id: 'role-screen-access',
        label: 'Role Screen Access',
        icon: 'LayoutGrid',
        path: '/settings/role-screen-access',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'roles:read'],
        ariaLabel: 'Configure which screens each role can access',
        mobileVisibility: 'show',
        searchKeywords: ['roles', 'screens', 'access', 'permissions', 'rbac', 'control'],
        searchPriority: 38,
        telemetryId: 'nav_role_screen_access',
      },
      {
        id: 'hierarchy-manager',
        label: 'Hierarchy Manager',
        icon: 'Network',
        path: '/enterprise/hierarchy',
        category: 'Organization Administration',
        requiredModule: 'mvp0_enterprise',
        requiredPermission: ['*.*', 'hierarchy:manage'],
        ariaLabel: 'Manage organisational hierarchy — estates, divisions, plots',
        mobileVisibility: 'show',
        searchKeywords: ['hierarchy', 'tree', 'structure', 'nodes', 'estate', 'division'],
        searchPriority: 38,
        telemetryId: 'nav_hierarchy_manager',
      },
      {
        id: 'approved-assets',
        label: 'Approved Assets',
        icon: 'Package',
        path: '/settings/approved-assets',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'assets:manage'],
        ariaLabel: 'Manage approved drones, sensors, and cameras',
        mobileVisibility: 'show',
        searchKeywords: ['assets', 'hardware', 'drones', 'sensors', 'cameras', 'registry', 'calibration'],
        searchPriority: 37,
        telemetryId: 'nav_approved_assets',
      },
      {
        id: 'units-of-measure',
        label: 'Units & ROI Parameters',
        icon: 'Ruler',
        path: '/settings/units-of-measure',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'settings:write'],
        ariaLabel: 'Configure currency, volume units, area units, and value realisation parameters',
        mobileVisibility: 'show',
        searchKeywords: ['units', 'uom', 'currency', 'bbl', 'hectares', 'acres', 'roi', 'value', 'crude price', 'fine', 'emissions'],
        searchPriority: 36,
        telemetryId: 'nav_uom_settings',
      },
      {
        id: 'flight-data-pipeline',
        label: 'Flight Data Pipeline',
        icon: 'HardDrive',
        path: '/settings/flight-data-pipeline',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'settings:write'],
        ariaLabel: 'Configure how drone imagery is processed and uploaded after survey missions',
        mobileVisibility: 'show',
        searchKeywords: ['pipeline', 'raw', 'images', 'upload', 'processed', 'orthomosaic', 'odm', 'photogrammetry', 'drone', 'imagery', 'jpeg', 'dng', 'tiff'],
        searchPriority: 35,
        telemetryId: 'nav_flight_pipeline_settings',
      },
      {
        id: 'satellite-monitoring',
        label: 'Satellite Monitoring Schedule',
        icon: 'Satellite',
        path: '/settings/satellite-monitoring',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'settings:write'],
        ariaLabel: 'Configure automated satellite scan frequency, cloud-cover threshold, and sweep scope',
        mobileVisibility: 'show',
        searchKeywords: ['satellite', 'scan', 'frequency', 'monitoring', 'schedule', 'sentinel', 'gee', 'cloud', 'cron', 'sweep', 'ndvi', 'cadence'],
        searchPriority: 36,
        telemetryId: 'nav_satellite_monitoring_settings',
      },
      {
        id: 'ai-configuration',
        label: 'AI Configuration',
        icon: 'Brain',
        path: '/settings/ai-configuration',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin'],
        ariaLabel: 'Configure AI provider, model selection, and usage budgets',
        mobileVisibility: 'show',
        searchKeywords: ['ai', 'llm', 'gemini', 'openai', 'configuration', 'api key', 'model'],
        searchPriority: 36,
        telemetryId: 'nav_ai_configuration',
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        icon: 'Webhook',
        path: '/enterprise/webhooks',
        category: 'Organization Administration',
        requiredModule: 'mvp0_enterprise',
        requiredPermission: ['*.*', 'webhooks:manage'],
        ariaLabel: 'Configure outbound webhook integrations for ERP and external systems',
        mobileVisibility: 'show',
        searchKeywords: ['webhooks', 'integrations', 'api', 'events', 'erp', 'sap'],
        searchPriority: 35,
        telemetryId: 'nav_webhooks',
      },
      {
        id: 'notifications',
        label: 'Notifications',
        icon: 'Bell',
        path: '/enterprise/notifications',
        category: 'Organization Administration',
        requiredModule: 'mvp0_enterprise',
        requiredPermission: ['*.*', 'notifications:manage'],
        ariaLabel: 'Manage notification channels — email, SMS, push, WhatsApp',
        mobileVisibility: 'show',
        searchKeywords: ['notifications', 'alerts', 'email', 'sms', 'slack', 'whatsapp', 'push'],
        searchPriority: 34,
        telemetryId: 'nav_notifications',
      },
      {
        id: 'audit-trail',
        label: 'Audit Trail',
        icon: 'FileText',
        path: '/enterprise/audit',
        category: 'Organization Administration',
        requiredModule: 'mvp0_enterprise',
        requiredPermission: ['*.*', 'audit:view'],
        ariaLabel: 'View immutable audit logs of all AI decisions and system actions',
        mobileVisibility: 'show',
        searchKeywords: ['audit', 'logs', 'trail', 'compliance', 'history', 'immutable'],
        searchPriority: 33,
        telemetryId: 'nav_audit_trail',
      },
      {
        id: 'integrity-dashboard',
        label: 'Data Integrity',
        icon: 'ShieldCheck',
        path: '/enterprise/integrity',
        category: 'Organization Administration',
        requiredModule: 'mvp0_enterprise',
        requiredPermission: ['*.*', 'integrity:view'],
        ariaLabel: 'Monitor cryptographic data integrity — SHA-256 seals and tamper alerts',
        mobileVisibility: 'show',
        searchKeywords: ['integrity', 'checksum', 'validation', 'data', 'sha256', 'tamper'],
        searchPriority: 32,
        telemetryId: 'nav_integrity_dashboard',
      },
      {
        id: 'mfa-management',
        label: 'Security & MFA',
        icon: 'Lock',
        path: '/enterprise/mfa',
        category: 'Organization Administration',
        requiredPermission: ['*.*', 'organization:admin', 'security:manage'],
        requiredModule: 'mvp0_enterprise',
        ariaLabel: 'Multi-factor authentication and security policy settings',
        mobileVisibility: 'show',
        searchKeywords: ['mfa', 'security', '2fa', 'authentication', 'totp', 'sms'],
        searchPriority: 30,
        telemetryId: 'nav_mfa_management',
      },
    ],
  },

  // ===========================================================================
  // PLATFORM ADMINISTRATION  (Platform Admin only — bottom of nav)
  // ===========================================================================
  {
    id: 'platform-management',
    label: 'Platform',
    icon: 'Shield',
    category: 'Platform Administration',
    requiredPermission: '*.*',
    ariaLabel: 'Platform management for super-administrators',
    mobileVisibility: 'collapse',
    searchKeywords: ['platform', 'admin', 'superadmin', 'global'],
    searchPriority: 100,
    telemetryId: 'nav_platform_management',
    dividerAfter: true,
    children: [
      {
        id: 'platform-dashboard',
        label: 'Platform Dashboard',
        icon: 'LayoutDashboard',
        path: '/platform-admin/dashboard',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Platform admin overview — pending actions and recent activity',
        mobileVisibility: 'show',
        searchKeywords: ['platform', 'dashboard', 'overview', 'pending', 'activity'],
        searchPriority: 100,
        telemetryId: 'nav_platform_dashboard',
      },
      {
        id: 'organization-directory',
        label: 'Organisation Directory',
        icon: 'Building2',
        path: '/platform-admin/organizations',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Manage all organisations and their enabled verticals',
        mobileVisibility: 'show',
        searchKeywords: ['organizations', 'tenants', 'provision', 'directory', 'verticals'],
        searchPriority: 95,
        telemetryId: 'nav_org_directory',
      },
      {
        id: 'access-management',
        label: 'Access Management',
        icon: 'ShieldCheck',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Users, roles, and membership management',
        mobileVisibility: 'show',
        searchKeywords: ['access', 'users', 'roles', 'membership', 'permissions'],
        searchPriority: 90,
        telemetryId: 'nav_access_management',
        children: [
          {
            id: 'membership-requests',
            label: 'Membership Requests',
            icon: 'UserPlus',
            path: '/platform-admin/membership-requests',
            category: 'Platform Administration',
            requiredPermission: '*.*',
            ariaLabel: 'Review and approve user membership requests',
            mobileVisibility: 'show',
            searchKeywords: ['membership', 'requests', 'approval', 'join'],
            searchPriority: 90,
            telemetryId: 'nav_membership_requests',
            badge: {
              type: 'count',
              source: 'api_endpoint',
              endpoint: '/api/membership-requests?status=pending',
              color: 'bg-amber-500',
            },
          },
          {
            id: 'platform-user-management',
            label: 'User Management',
            icon: 'Users',
            path: '/platform-admin/users',
            category: 'Platform Administration',
            requiredPermission: '*.*',
            ariaLabel: 'Manage users across all organisations',
            mobileVisibility: 'show',
            searchKeywords: ['users', 'management', 'platform', 'global'],
            searchPriority: 88,
            telemetryId: 'nav_platform_user_management',
          },
          {
            id: 'user-management-audit',
            label: 'User Management Audit',
            icon: 'History',
            path: '/platform-admin/user-audit',
            category: 'Platform Administration',
            requiredPermission: '*.*',
            ariaLabel: 'View user management audit trail',
            mobileVisibility: 'show',
            searchKeywords: ['audit', 'trail', 'logs', 'user', 'management', 'history'],
            searchPriority: 87,
            telemetryId: 'nav_user_management_audit',
          },
          {
            id: 'role-registry',
            label: 'Role Registry',
            icon: 'Key',
            path: '/platform-admin/roles',
            category: 'Platform Administration',
            requiredPermission: '*.*',
            ariaLabel: 'Manage dynamic role definitions and permissions',
            mobileVisibility: 'show',
            searchKeywords: ['roles', 'permissions', 'registry', 'rbac'],
            searchPriority: 85,
            telemetryId: 'nav_role_registry',
          },
        ],
      },
      {
        id: 'platform-analytics',
        label: 'Platform Analytics',
        icon: 'BarChart3',
        path: '/platform-admin/analytics',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Cross-tenant analytics and metrics',
        mobileVisibility: 'show',
        searchKeywords: ['analytics', 'metrics', 'usage', 'billing'],
        searchPriority: 80,
        telemetryId: 'nav_platform_analytics',
      },
      {
        id: 'vertical-marketplace',
        label: 'Vertical Marketplace',
        icon: 'Package',
        path: '/platform-admin/verticals',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Enable and manage industry verticals per organisation',
        mobileVisibility: 'show',
        searchKeywords: ['verticals', 'modules', 'marketplace', 'agrotech', 'oil gas', 'infrastructure', 'environmental'],
        searchPriority: 75,
        telemetryId: 'nav_vertical_marketplace',
      },
      {
        id: 'demo-setup',
        label: 'Demo Setup',
        icon: 'FlaskConical',
        path: '/platform-admin/demo-setup',
        category: 'Platform Administration',
        requiredPermission: '*.*',
        ariaLabel: 'Set up demo environments — seed data, sample files, and interactive runbooks',
        mobileVisibility: 'show',
        searchKeywords: ['demo', 'setup', 'seed', 'sample', 'runbook', 'sales', 'presentation', 'test data'],
        searchPriority: 70,
        telemetryId: 'nav_demo_setup',
      },
    ],
  },
];

// ============================================================================
// VERTICAL MODULE DEFINITIONS
// Keys here are the canonical module IDs used in:
//  - NavigationConfig.requiredModule
//  - organizations.enabled_modules[]
//  - OrganizationProvisioningPage vertical selector
//
// Each vertical owns the full 9-step intelligence loop in its nav group.
// Shared React components (BoundaryEditor, TemporalAnalysis, AnomalyAlerts,
// MissionPlanner, DroneAnalysis, RemediationPlanner) are parameterised by
// vertical context — one codebase, five nav contexts.
// ============================================================================

export interface VerticalModule {
  id: string;
  label: string;
  description: string;
  icon: string;
  /** 'core' = always-on platform module; 'available' = enabled/disabled per org; 'coming_soon' = not yet purchasable */
  status: 'core' | 'available' | 'coming_soon';
  navGroups: string[];
}

export const VERTICAL_MODULES: VerticalModule[] = [
  // ── Core Platform (always enabled; cannot be toggled) ──────────────────────
  {
    id: 'mvp0_core',
    label: 'Core Platform',
    description:
      'Foundation module: Command Center, Mission Control, Spatial Map View, Ask Nabhos NLQ, and all base data pipelines. Required for all organisations.',
    icon: 'Layers',
    status: 'core',
    navGroups: ['command-center'],
  },
  {
    id: 'mvp0_enterprise',
    label: 'Enterprise Features',
    description:
      'HITL Certification, Hierarchy Manager, Audit Trail, Data Integrity (SHA-256 seals), MFA, Webhooks, and Notifications. Required for all organisations.',
    icon: 'ShieldCheck',
    status: 'core',
    navGroups: [],
  },

  // ── Purchasable Verticals ──────────────────────────────────────────────────
  {
    id: 'mvp1_agrotech',
    label: 'AgroTech — Estate Intelligence',
    description:
      'Full satellite-to-remediation loop: field boundary definition, NDVI/NDRE/NDWI temporal monitoring, AI anomaly analysis, drone survey planning (multispectral/thermal), post-flight diagnosis, human verification, and precision remediation missions.',
    icon: 'Leaf',
    status: 'available',
    navGroups: ['agrotech-estate'],
  },
  {
    id: 'mvp2_agrotech_precision',
    label: 'AgroTech — Precision Planning',
    description:
      'Variable-rate prescription maps (ISOXML), yield forecasting, crop suitability indexing (Kiwi, Avocado), land expansion ROI modelling, and chemical compliance guardrails.',
    icon: 'Target',
    status: 'available',
    navGroups: ['agrotech-precision'],
  },
  {
    id: 'mvp3_oil_gas',
    label: 'Oil & Gas Intelligence',
    description:
      'Pipeline corridor satellite monitoring, vegetation die-off + thermal leak indicators, OGI drone survey planning, methane hotspot diagnosis, incident response (pressure test / shut-in), and upstream exploration analysis.',
    icon: 'Flame',
    status: 'available',
    navGroups: ['oil-gas'],
  },
  {
    id: 'mvp4_infrastructure',
    label: 'Infrastructure Intelligence',
    description:
      'Linear asset corridor monitoring, unauthorized construction detection (NDBI), earthwork volume analysis (RGB + LiDAR), slope instability assessment, and compliance enforcement workflow.',
    icon: 'Building',
    status: 'available',
    navGroups: ['infrastructure'],
  },
  {
    id: 'mvp5_environmental',
    label: 'Environmental Geotech',
    description:
      'Protected zone monitoring (Ramsar wetlands, national parks, forest reserves, carbon credit parcels), deforestation and wetland encroachment detection via NDVI/NDWI/NBR indices, illegal mining identification, fixed-wing canopy drone surveys, enforcement authority notification, carbon credit biomass verification, and geohazard risk assessment (landslide, flood, slope stability via SAR coherence).',
    icon: 'TreePine',
    status: 'available',
    navGroups: ['environmental'],
  },
  {
    id: 'mvp6_linear_assets',
    label: 'Linear Assets (Power & Rail)',
    description:
      'Power line and railway right-of-way corridor monitoring, predictive vegetation clearance modeling (growth rate m/month, days-to-contact), rapid track obstruction triage (<6h SLA), LiDAR drone patrols, and vegetation clearance work order dispatch.',
    icon: 'Zap',
    status: 'available',
    navGroups: ['linear-assets-vertical'],
  },
];

// Made with Bob
